import { Component, ChangeDetectionStrategy, OnInit, OnDestroy, signal, inject, computed } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { DragDropModule, CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { NzModalModule } from 'ng-zorro-antd/modal';
import { FikoYTKokBilesenComponent } from '@service/kok-bilesen/kok-bilesen.component';
import { PanoDurumService } from '@uygulamalar/hmi-pano/services/pano-durum.service';
import { SablonRendererComponent } from '@/src/service/fikoyt-ui/src/lib/paylasim-sistemi/sablon-renderer/sablon-renderer.component';
import { DashboardService } from './dashboard.service';
import type { ISablon } from '@/src/service/fikoyt-ui/src/lib/paylasim-sistemi/sablon-editoru/sablon.interface';
import { PdksAktifKartComponent } from '../pano/pages/personel/pages/pdks/pdks-aktif-kart.component';

interface IUygulamaKart {
  baslik: string;
  aciklama: string;
  ikon: string;
  route: string;
  renk: string;
  gradient: string;
  yetkiler?: string[];
}

@Component({
  selector: 'hmi-dashboard',
  standalone: true,
  imports: [CommonModule, RouterModule, DragDropModule, NzButtonModule, NzSpinModule, NzModalModule, SablonRendererComponent, PdksAktifKartComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './dashboard.component.html',
  styleUrl: './dashboard.component.less',
})
export class HmiDashboardComponent extends FikoYTKokBilesenComponent implements OnInit, OnDestroy {
  private dashboardSvc = inject(DashboardService);
  private durum = inject(PanoDurumService);

  duzenleModu = signal(false);
  tumWidgetler = computed(() => this.dashboardSvc.tumWidgetler());

  readonly kartlar: IUygulamaKart[] = [
    { baslik: 'Projeler', aciklama: 'Proje takibi ve yonetimi', ikon: '\uD83D\uDCC1', route: 'projeler', renk: '#fa8c16', gradient: 'linear-gradient(135deg, #7c2d12 0%, #fa8c16 100%)' },
    { baslik: '\u00DCretim', aciklama: '\u0130\u015F emri, kalite kontrol, \u00FCretim takibi', ikon: '\u2699\uFE0F', route: 'uretim', renk: '#f5222d', gradient: 'linear-gradient(135deg, #7f1d1d 0%, #f5222d 100%)' },
    { baslik: 'Sat\u0131\u015F', aciklama: 'Sipari\u015F, teklif, m\u00FC\u015Fteri y\u00F6netimi', ikon: '\uD83D\uDED2', route: 'satis', renk: '#ff9800', gradient: 'linear-gradient(135deg, #e65100 0%, #ff9800 100%)' },
    { baslik: 'Y\u00F6netim', aciklama: 'Personel, departman, tedarik\u00E7i', ikon: '\uD83C\uDFE2', route: 'yonetim-kayit', renk: '#722ed1', gradient: 'linear-gradient(135deg, #4a148c 0%, #722ed1 100%)' },
    { baslik: 'Posta', aciklama: 'Firma mail kutusunu g\u00F6r\u00FCnt\u00FCle', ikon: '\uD83D\uDCE7', route: 'posta', renk: '#1890ff', gradient: 'linear-gradient(135deg, #0c4a6e 0%, #1890ff 100%)' },
  ];

  constructor() { super(); }

  ngOnInit() {
    const cihazKimlik = localStorage.getItem('fikoyt_cihaz_kimlik');
    if (cihazKimlik) {
      this.dashboardSvc.yukle(cihazKimlik);
    }
  }

  override ngOnDestroy() {
    super.ngOnDestroy();
    this.dashboardSvc.temizle();
  }

  kartGorunurMu(kart: IUygulamaKart): boolean {
    if (!kart.yetkiler?.length) return true;
    const kullaniciYetkileri = this.durum.kisiYetkileri();
    return kart.yetkiler.some(y => kullaniciYetkileri.includes(y));
  }

  onDrop(event: CdkDragDrop<ISablon[]>) {
    const widgetler = [...this.tumWidgetler()];
    moveItemInArray(widgetler, event.previousIndex, event.currentIndex);
    this.dashboardSvc.dbWidgetler.set(widgetler.map((w, i) => ({ ...w, sira: i, kaynak: 'db' as const })));
  }

  async kaydet() {
    const cihazKimlik = localStorage.getItem('fikoyt_cihaz_kimlik');
    if (cihazKimlik) {
      await this.dashboardSvc.kaydet(cihazKimlik, this.tumWidgetler());
      this.nzMessage.success('Dashboard kaydedildi');
      this.duzenleModu.set(false);
    }
  }

  sablonSil(id: string) {
    this.nzModalServis.confirm({
      nzTitle: 'Widget silinsin mi?',
      nzOnOk: () => this.dashboardSvc.sablonSil(id),
    });
  }

  yeniSablonEkle() {
    const yeniSablon: ISablon = {
      id: 'custom-' + Date.now(),
      tur: 'widget',
      baslik: 'Yeni Widget',
      simge: '\uD83D\uDCE6',
      mod: 'serbest',
      arkaPlan: { tur: 'renk', deger: 'transparent' },
      bolgeler: [],
      elementler: [],
      pozisyon: { genislik: 1, yukseklik: 1 },
      stil: { gradyan: 'linear-gradient(135deg, #0f172a, #1e293b)', renkYazi: '#fff' },
      veriKaynagi: { mod: 'sabit', sabitVeri: { mesaj: 'Yeni widget' } },
      kaynak: 'db',
      gorunur: true,
      sira: this.tumWidgetler().length,
    };
    this.dashboardSvc.sablonEkle(yeniSablon);
    this.nzMessage.success('Widget eklendi');
  }

  sablonDuzenle(sablon: ISablon) {
    this.router.navigate(['widget-editor', sablon.id], { relativeTo: this.route.parent });
  }

  layoutSifirla() {
    this.nzModalServis.confirm({
      nzTitle: 'Layout sifirlansin mi?',
      nzContent: 'Tum ozellestirmeler silinecek.',
      nzOnOk: () => { this.dashboardSvc.layoutSifirla(); this.nzMessage.info('Layout sifirlandi'); },
    });
  }
}
